/**
 * Per-user timesheet config — stored in localStorage today, on
 * `public.users` columns once they exist (per BACKLOG).
 */

import type { OtPremiumMethod } from './allocateOtPremium'

const GRACE_KEY = 'ts-grace-period-hours'

export const GRACE_PERIOD_OPTIONS = [24, 48, 72, 168] as const
export type GracePeriodHours = (typeof GRACE_PERIOD_OPTIONS)[number]

const DEFAULT_GRACE = 168

/**
 * Human label for a grace-period value — whole weeks/days read as "1 week" /
 * "3 days" rather than a raw hour count; everything else stays in hours.
 */
export function formatGracePeriod(hours: number): string {
  if (hours % 168 === 0) {
    const weeks = hours / 168
    return `${weeks} week${weeks === 1 ? '' : 's'}`
  }
  if (hours % 24 === 0) {
    const days = hours / 24
    return `${days} day${days === 1 ? '' : 's'}`
  }
  return `${hours} hours`
}

export function getStoredGracePeriod(): number {
  try {
    const raw = localStorage.getItem(GRACE_KEY)
    if (!raw) return DEFAULT_GRACE
    const n = Number(raw)
    if (!Number.isFinite(n) || n <= 0) return DEFAULT_GRACE
    return n
  } catch {
    return DEFAULT_GRACE
  }
}

export function setStoredGracePeriod(hours: number): void {
  try {
    localStorage.setItem(GRACE_KEY, String(hours))
    // Notify other windows / hooks listening for the change. localStorage
    // events don't fire in the same tab — emit a CustomEvent instead.
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: GRACE_KEY } }))
  } catch { /* non-fatal */ }
}

// ── Business timezone ────────────────────────────────────────────────────────
// The org's operating timezone drives "today" + day-window math (see
// lib/azDate.ts). Configurable so the template demos correctly for a company in
// any zone; a customer fork pins it to their org. Default is Arizona (the
// original single-org assumption). Stored per-user in localStorage today; moves
// to an org/users column when those land (same path as the grace period).
const BUSINESS_TZ_KEY = 'ts-business-timezone'
export const DEFAULT_BUSINESS_TZ = 'America/Phoenix'

/** Common US zones + UTC for the Settings picker. Any valid IANA id works. */
export const BUSINESS_TZ_OPTIONS = [
  { id: 'America/New_York', label: 'Eastern (New York)' },
  { id: 'America/Chicago', label: 'Central (Chicago)' },
  { id: 'America/Denver', label: 'Mountain (Denver)' },
  { id: 'America/Phoenix', label: 'Arizona (Phoenix, no DST)' },
  { id: 'America/Los_Angeles', label: 'Pacific (Los Angeles)' },
  { id: 'America/Anchorage', label: 'Alaska (Anchorage)' },
  { id: 'Pacific/Honolulu', label: 'Hawaii (Honolulu)' },
  { id: 'UTC', label: 'UTC' },
] as const

export function getStoredBusinessTz(): string {
  try {
    const raw = localStorage.getItem(BUSINESS_TZ_KEY)
    if (!raw) return DEFAULT_BUSINESS_TZ
    // Validate it's a usable IANA zone before trusting it.
    new Intl.DateTimeFormat('en-US', { timeZone: raw })
    return raw
  } catch {
    return DEFAULT_BUSINESS_TZ
  }
}

export function setStoredBusinessTz(tz: string): void {
  try {
    localStorage.setItem(BUSINESS_TZ_KEY, tz)
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: BUSINESS_TZ_KEY } }))
  } catch { /* non-fatal */ }
}

// ── Government-contracting mode (A2) ──────────────────────────────────────────
// When ON, gov-contracting charge-code axes (ACRN, color of money, indirect
// pool, PoP, allowability) are available; when OFF, the picker + displays show a
// clean commercial/civilian coordinate. Default ON — the deployed demo is an
// aerospace/defense (gov) profile; a civilian demo/fork flips it OFF.
const GOV_CONTRACTING_KEY = 'ts-government-contracting'
export const DEFAULT_GOV_CONTRACTING = true

export function getStoredGovContracting(): boolean {
  try {
    const raw = localStorage.getItem(GOV_CONTRACTING_KEY)
    if (raw === 'true') return true
    if (raw === 'false') return false
    return DEFAULT_GOV_CONTRACTING
  } catch {
    return DEFAULT_GOV_CONTRACTING
  }
}

export function setStoredGovContracting(enabled: boolean): void {
  try {
    localStorage.setItem(GOV_CONTRACTING_KEY, enabled ? 'true' : 'false')
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: GOV_CONTRACTING_KEY } }))
  } catch { /* non-fatal */ }
}

// ── Charge-line field visibility (Phase 4) ───────────────────────────────────
// Per-user show/hide of the multi-dimensional "Choose Charge Line" fields that
// surface in the picker + on the grid row. Stored as the set of HIDDEN field
// keys, so the default (no override) shows every field flagged `defaultVisible`.
// Each key is a key of ResolvedChargeCodeDimensions so the picker/grid can read
// the field directly. `gov` fields are additionally gated by the gov flag.
const CHARGE_LINE_FIELDS_KEY = 'ts-charge-line-hidden-fields'

export interface ChargeLineFieldMeta {
  /** Key of ResolvedChargeCodeDimensions this field renders. */
  key: string
  label: string
  defaultVisible: boolean
  /** Government-contracting-only field (hidden when the gov flag is off). */
  gov?: boolean
}

/** The toggleable "Choose Charge Line" display fields, in display order. */
export const CHARGE_LINE_FIELDS: ChargeLineFieldMeta[] = [
  { key: 'organizationLabel', label: 'Organization', defaultVisible: true },
  { key: 'disciplineLabel', label: 'Discipline (SAP activity type)', defaultVisible: true },
  { key: 'timeTypeLabel', label: 'Time type (attendance / absence)', defaultVisible: true },
  { key: 'costObjectiveLabel', label: 'Cost objective', defaultVisible: true },
  { key: 'colorOfMoney', label: 'Color of money', defaultVisible: false, gov: true },
  { key: 'acrn', label: 'ACRN', defaultVisible: false, gov: true },
  { key: 'indirectPool', label: 'Indirect pool', defaultVisible: false, gov: true },
  { key: 'workSite', label: 'Work site', defaultVisible: false },
  { key: 'capitalOrExpense', label: 'Capital / expense', defaultVisible: false },
]

function readHiddenFields(): Set<string> {
  try {
    const raw = localStorage.getItem(CHARGE_LINE_FIELDS_KEY)
    if (!raw) {
      // No override yet — hidden set is every field NOT default-visible.
      return new Set(CHARGE_LINE_FIELDS.filter((f) => !f.defaultVisible).map((f) => f.key))
    }
    const arr = JSON.parse(raw)
    return new Set(Array.isArray(arr) ? (arr as string[]) : [])
  } catch {
    return new Set(CHARGE_LINE_FIELDS.filter((f) => !f.defaultVisible).map((f) => f.key))
  }
}

/** The set of charge-line field keys the user has chosen to show. */
export function getVisibleChargeLineFields(): Set<string> {
  const hidden = readHiddenFields()
  return new Set(CHARGE_LINE_FIELDS.filter((f) => !hidden.has(f.key)).map((f) => f.key))
}

export function isChargeLineFieldVisible(key: string): boolean {
  return !readHiddenFields().has(key)
}

export function setChargeLineFieldVisible(key: string, visible: boolean): void {
  try {
    const hidden = readHiddenFields()
    if (visible) hidden.delete(key)
    else hidden.add(key)
    localStorage.setItem(CHARGE_LINE_FIELDS_KEY, JSON.stringify([...hidden]))
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: CHARGE_LINE_FIELDS_KEY } }))
  } catch { /* non-fatal */ }
}

// ── Time approval methodology (Time Approval Routing, Part 1) ─────────────────
// How a submitted weekly timesheet is routed for approval:
//   'manager'       — today's behavior: the worker's users.manager_id approves
//                     the whole weekly submission (zero change; the default).
//   'charge_object' — each timesheet LINE routes to the approver assigned to its
//                     charge object (charge_object_approvers). A week with lines
//                     across multiple charge objects fans out to one approval
//                     segment per approver; the submission is approved only when
//                     every segment is. An unrouted line falls back to the
//                     worker's manager so a timesheet is never unroutable.
// Org-wide, stored like the gov-contracting toggle. Default 'manager' so the
// whole feature is opt-in and there is zero behavior change until switched.
// Production path: an org config column or a config_versions key
// `approval.methodology` (effective-dated).
export type ApprovalMethodology = 'manager' | 'charge_object'
const APPROVAL_METHODOLOGY_KEY = 'ts-approval-methodology'
export const DEFAULT_APPROVAL_METHODOLOGY: ApprovalMethodology = 'manager'

export function getStoredApprovalMethodology(): ApprovalMethodology {
  try {
    const raw = localStorage.getItem(APPROVAL_METHODOLOGY_KEY)
    return raw === 'charge_object' ? 'charge_object' : 'manager'
  } catch {
    return DEFAULT_APPROVAL_METHODOLOGY
  }
}

export function setStoredApprovalMethodology(m: ApprovalMethodology): void {
  try {
    localStorage.setItem(APPROVAL_METHODOLOGY_KEY, m)
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: APPROVAL_METHODOLOGY_KEY } }))
  } catch { /* non-fatal */ }
}

// ── Charge-line dimension layout ──────────────────────────────────────────────
// How the multi-dimensional charge-line attributes (Organization, Discipline,
// Time type, …) render on the week grid: 'columns' = one grid column each;
// 'combined' = shown as chips inside the single Charge Line cell (compact — the
// attributes ARE the charge line). Per-user, localStorage. Mobile always uses
// 'combined' regardless of this preference so time entry stays clean on a phone.
export type ChargeLineDimLayout = 'columns' | 'combined'
const CHARGE_LINE_DIM_LAYOUT_KEY = 'ts-charge-line-dim-layout'
export const DEFAULT_CHARGE_LINE_DIM_LAYOUT: ChargeLineDimLayout = 'columns'

export function getChargeLineDimLayout(): ChargeLineDimLayout {
  try {
    const raw = localStorage.getItem(CHARGE_LINE_DIM_LAYOUT_KEY)
    return raw === 'combined' || raw === 'columns' ? raw : DEFAULT_CHARGE_LINE_DIM_LAYOUT
  } catch {
    return DEFAULT_CHARGE_LINE_DIM_LAYOUT
  }
}

export function setChargeLineDimLayout(v: ChargeLineDimLayout): void {
  try {
    localStorage.setItem(CHARGE_LINE_DIM_LAYOUT_KEY, v)
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: CHARGE_LINE_DIM_LAYOUT_KEY } }))
  } catch { /* non-fatal */ }
}

// ── OT-premium allocation method (#119) ───────────────────────────────────────
// How an hourly worker's overtime/double-time PREMIUM is allocated across the
// charge lines they worked (the straight-time base is always charged direct by
// hours; only the premium portion is in question — see lib/allocateOtPremium):
//   'spread_evenly' — the premium is blended over ALL hours worked (indirect /
//                     overhead treatment; the common default under FAR
//                     31.205-6(f)) so no single contract is penalized for the
//                     scheduling that caused the OT.
//   'tied_to_ot'    — the premium is charged DIRECT to the specific work that
//                     necessitated the overtime (chronological last-in, with a
//                     pro-rata fallback when a day carries no start times).
// Org-wide, stored like the approval methodology. Default 'spread_evenly' (the
// settled decision). Production path: a per-Disclosure-Statement setting on the
// CAS rate segment (today a free-text string), effective-dated.
export type { OtPremiumMethod }
const OT_PREMIUM_METHOD_KEY = 'ts-ot-premium-method'
export const DEFAULT_OT_PREMIUM_METHOD: OtPremiumMethod = 'spread_evenly'

export function getStoredOtPremiumMethod(): OtPremiumMethod {
  try {
    const raw = localStorage.getItem(OT_PREMIUM_METHOD_KEY)
    return raw === 'tied_to_ot' ? 'tied_to_ot' : 'spread_evenly'
  } catch {
    return DEFAULT_OT_PREMIUM_METHOD
  }
}

export function setStoredOtPremiumMethod(m: OtPremiumMethod): void {
  try {
    localStorage.setItem(OT_PREMIUM_METHOD_KEY, m)
    window.dispatchEvent(new CustomEvent('ts-config-changed', { detail: { key: OT_PREMIUM_METHOD_KEY } }))
  } catch { /* non-fatal */ }
}
